import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { Action, Actor, enrollmentTracking } from '../Integrations/Tracking'
import { stepNameForRoute } from '../Integrations/Tracking/stepRouteMap'
import { useQuestionStore } from '../Stores/question'

export function useInteractionTracking() {
    const route = useRoute()
    const questionStore = useQuestionStore()

    const currentTechniqueKey = computed<string | null | undefined>(
        () =>
            questionStore.techniquesQuestions.find(
                ({ techniqueId }) => techniqueId === route.params.uuid
            )?.key
    )

    const currentRenovationCategoryKey = computed<string | null | undefined>(
        () => questionStore.categories.find(({ id }) => id === route.params.uuid)?.key
    )

    function track(action: Action, props: Record<string, unknown>) {
        enrollmentTracking.track(Actor.Homeowner, action, {
            ...props,
            step_name: stepNameForRoute(
                route.name as string,
                currentTechniqueKey.value,
                currentRenovationCategoryKey.value
            ),
        })
    }

    function trackFillField(questionKey: string, filled: boolean) {
        track(Action.FillField, { question_key: questionKey, filled })
    }

    function trackSimulationField(techniqueKey: string, questionKey: string, value: number) {
        track(Action.FillField, { technique_key: techniqueKey, question_key: questionKey, value })
    }

    function trackSelectOption(questionKey: string, answerKey: string) {
        track(Action.SelectOption, {
            question_key: questionKey,
            answer_key: answerKey.split('.').pop(),
        })
    }

    function trackToggleOption(
        questionKey: string,
        answerKey: string,
        selected: boolean,
        techniqueKey?: string
    ) {
        track(Action.ToggleOption, {
            question_key: questionKey,
            answer_key: answerKey.split('.').pop(),
            selected,
            ...(techniqueKey && { technique_key: techniqueKey }),
        })
    }

    function trackToggleRenovationCategory(renovationCategoryKey: string, selected: boolean) {
        track(Action.ToggleOption, {
            renovation_category_key: renovationCategoryKey,
            selected,
        })
    }

    function trackToggleTechnique(
        techniqueKey: string,
        selected: boolean,
        panelCount: number | null,
        trigger: 'change' | 'load' = 'change'
    ) {
        track(Action.ToggleOption, {
            technique_key: techniqueKey,
            selected,
            panel_count: panelCount,
            trigger,
        })
    }

    function trackClickButton(extraProps: Record<string, unknown> = {}) {
        track(Action.ClickButton, extraProps)
    }

    return {
        trackFillField,
        trackSimulationField,
        trackSelectOption,
        trackToggleOption,
        trackToggleRenovationCategory,
        trackToggleTechnique,
        trackClickButton,
    }
}
